import type {
  ExperimentPatch,
  ExperimentStatusChange,
  OutcomePut,
  ResultsPut,
  VerdictPost,
} from '@radar/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api, apiUrl, call } from '@/lib/api';
import { type ExperimentsListFilters, qk } from '@/lib/query-keys';
import type { ExperimentsSearch } from '@/lib/search-schemas';

// Accès à `/api/v1/experiments` et aux routes d'expérience portées par l'opportunité
// (`api.md` § Expériences et résultats, fiche T26). Tout passe par le client typé sauf le brief,
// servi en `text/markdown`.

export type ExperimentsListData = Awaited<ReturnType<typeof listExperiments>>;
export type ExperimentDetailData = Awaited<ReturnType<typeof getExperiment>>;

/** Search params de `/experiments` → query de `GET /experiments` (valeurs en chaînes). */
export function toExperimentsQuery(search: ExperimentsSearch): ExperimentsListFilters {
  return {
    page: String(search.page),
    pageSize: String(search.pageSize),
    ...(search.status && search.status.length > 0 ? { status: search.status } : {}),
    ...(search.opportunityId ? { opportunityId: search.opportunityId } : {}),
    ...(search.ownerId ? { ownerId: search.ownerId } : {}),
  };
}

export function listExperiments(query: ExperimentsListFilters) {
  return call(api.experiments.$get({ query }));
}

export const experimentsListOptions = (query: ExperimentsListFilters) =>
  queryOptions({ queryKey: qk.experiments.list(query), queryFn: () => listExperiments(query) });

export function getExperiment(id: string) {
  return call(api.experiments[':id'].$get({ param: { id } }));
}

export const experimentDetailOptions = (id: string) =>
  queryOptions({ queryKey: qk.experiments.detail(id), queryFn: () => getExperiment(id) });

export function patchExperiment(id: string, json: ExperimentPatch) {
  return call(api.experiments[':id'].$patch({ param: { id }, json }));
}

export function changeExperimentStatus(id: string, json: ExperimentStatusChange) {
  return call(api.experiments[':id'].status.$post({ param: { id }, json }));
}

export function putExperimentResults(id: string, json: ResultsPut) {
  return call(api.experiments[':id'].results.$put({ param: { id }, json }));
}

export function postExperimentVerdict(id: string, json: VerdictPost) {
  return call(api.experiments[':id'].verdict.$post({ param: { id }, json }));
}

export function createExperiment(opportunityId: string) {
  // Corps optionnel côté serveur : le brouillon est entièrement pré-rempli (T26).
  return call(api.opportunities[':id'].experiments.$post({ param: { id: opportunityId } }));
}

export function putOutcome(opportunityId: string, json: OutcomePut) {
  return call(api.opportunities[':id'].outcome.$put({ param: { id: opportunityId }, json }));
}

/** URL absolue du brief : bouton « Ouvrir » (`window.open`) et lecture du texte. */
export function briefUrl(id: string): string {
  return apiUrl(`/experiments/${encodeURIComponent(id)}/brief`);
}

/** `GET /experiments/:id/brief` : Markdown brut (le client typé ne gère que le JSON). */
export async function getExperimentBrief(id: string): Promise<string> {
  const res = await fetch(briefUrl(id), { credentials: 'include' });
  if (!res.ok) throw new Error(`brief_unavailable_${res.status}`);
  return res.text();
}

export const experimentBriefOptions = (id: string) =>
  queryOptions({
    queryKey: qk.experiments.brief(id),
    queryFn: () => getExperimentBrief(id),
    staleTime: 0,
  });
